"use client";

import { Fragment, useEffect, useState } from "react";
import { motion } from "framer-motion";

// yS in the source: HH:MM:SS countdown to a target date.
function Countdown({
  targetDate,
  className,
}: {
  targetDate?: string;
  className?: string;
}) {
  const [text, setText] = useState("--:--:--");
  useEffect(() => {
    if (!targetDate) return;
    const tick = () => {
      const diff = new Date(targetDate).getTime() - Date.now();
      if (diff <= 0) {
        setText("00:00:00");
        return;
      }
      const h = Math.floor(diff / 3.6e6);
      const m = Math.floor((diff % 3.6e6) / 6e4);
      const s = Math.floor((diff % 6e4) / 1e3);
      setText(
        `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(
          s,
        ).padStart(2, "0")}`,
      );
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetDate]);
  return <span className={className}>{text}</span>;
}

type Stat = {
  text: string;
  accent?: boolean;
  countdown?: string;
  fallback?: string;
};

export function Ticker({
  activeLots,
  unitsAvailable,
  categoryCount,
  nextDropTime,
}: {
  activeLots: number;
  unitsAvailable: number;
  categoryCount: number;
  nextDropTime?: string;
}) {
  const stats: Stat[] = [
    { text: "LIVE NOW", accent: true },
    { text: `${activeLots} ACTIVE LOTS` },
    { text: `${unitsAvailable.toLocaleString()} UNITS AVAILABLE` },
    { text: `${categoryCount} CATEGORIES` },
    { text: "NEXT DROP IN", countdown: nextDropTime, fallback: "02:14:36" },
    { text: "MEMBER ACCESS OPEN", accent: true },
  ];

  const row = (
    <div className="flex items-center gap-6 px-3">
      {stats.map((h, p) => (
        <Fragment key={p}>
          <span
            className={`text-xs font-mono tracking-widest whitespace-nowrap ${
              h.accent ? "text-green-400" : "text-gray-400"
            }`}
          >
            {h.text}
            {h.countdown !== undefined && (
              <span className="text-white font-bold ml-2">
                {h.countdown ? (
                  <Countdown targetDate={h.countdown} />
                ) : (
                  h.fallback
                )}
              </span>
            )}
          </span>
          {p < stats.length - 1 && (
            <span className="w-1 h-1 bg-gray-700 rounded-full flex-shrink-0" />
          )}
        </Fragment>
      ))}
    </div>
  );

  return (
    <div className="border-y border-white/5 bg-[#0A0A0A] py-3 overflow-hidden">
      <motion.div
        className="flex w-max"
        animate={{ x: ["0%", "-50%"] }}
        transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
      >
        {row}
        {row}
      </motion.div>
    </div>
  );
}
